<template>
  <div class="ai-report-container">
    <!-- 动态背景元素 -->
    <div class="bg-effects">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
      <div class="pulse-ring"></div>
    </div>

    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 顶部选项卡 -->
      <div class="tab-group">
        <button v-for="tab in tabs" :key="tab.id" class="tab-button" :class="{ active: activeTab === tab.id }"
          @click="handleTabChange(tab.id)">
          <Icon :icon="tab.icon" class="tab-icon" />
          <span class="tab-label">{{ tab.label }}</span>
          <div class="tab-glow"></div>
        </button>
      </div>

      <!-- 数据概览区域 -->
      <div class="overview-section">
        <div class="data-total">
          <div v-for="(item, index) in dashboardStats" :key="index" class="total-item"
            :style="`--delay: ${index * 0.1}s`">
            <Icon :icon="item.icon" class="total-icon" />
            <div class="total-content">
              <div class="total-label">{{ item.label }}</div>
              <div class="total-value">{{ item.value }}</div>
              <div class="total-trend" :class="item.trend">
                <Icon :icon="item.trend === 'up' ? 'mdi:trending-up' :
                  item.trend === 'down' ? 'mdi:trending-down' : 'mdi:trending-neutral'"
                  class="trend-icon" />
                <span class="trend-text">{{ item.change }}%</span>
              </div>
            </div>
            <div class="total-glow"></div>
          </div>
        </div>
      </div>

      <!-- 主要内容区域 -->
      <div class="content-section">
        <!-- 左侧核心词榜单 -->
        <div class="left-panel neon-panel">
          <div class="panel-header">
            <span class="panel-title">
              <Icon icon="mdi:fire" class="panel-title-icon" />
              核心词榜单
            </span>
            <div class="panel-actions">
              <button class="action-btn" @click="loadCoreRankings">
                <Icon icon="mdi:refresh" class="action-icon" />
              </button>
            </div>
          </div>
          <div class="ranking-list">
            <div v-if="coreRankingsLoading" class="loading-state">
              <div class="loading-spinner"></div>
              <span>加载中...</span>
            </div>
            <div v-else-if="coreRankings.length === 0" class="empty-state">
              <Icon icon="mdi:chart-line" class="empty-icon" />
              <span>暂无数据</span>
            </div>
            <div v-else class="ranking-item" v-for="(item, index) in coreRankings" :key="index"
              :style="`--rank: ${index + 1}`">
              <div class="rank-badge" :class="`rank-${index + 1}`">
                <span class="rank-number">{{ index + 1 }}</span>
                <div class="rank-crown" v-if="index < 3">
                  <Icon icon="mdi:crown" class="crown-icon" />
                </div>
              </div>
              <div class="rank-content">
                <div class="name">{{ item.name }}</div>
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: item.percentage + '%' }"></div>
                </div>
              </div>
              <div class="count">{{ item.count }}</div>
              <div class="rank-sparkle"></div>
            </div>
          </div>
        </div>

        <!-- 右侧数据区域 -->
        <div class="right-panel">
          <!-- 训练平台分布 -->
          <div class="distilled-section neon-panel">
            <div class="panel-header">
              <span class="panel-title">
                <Icon icon="mdi:flask-outline" class="panel-title-icon" />
                训练平台分布
              </span>
            </div>
            <div class="distilled-content">
              <div v-if="trainingStatsLoading" class="loading-state">
                <div class="loading-spinner"></div>
              </div>
              <div v-else-if="trainingStats.length === 0" class="empty-state">
                <Icon icon="mdi:flask-outline" class="empty-icon" />
                <span>暂无数据</span>
              </div>
              <div v-else class="distilled-item" v-for="(item, index) in trainingStats" :key="index"
                :style="`--item-index: ${index}`">
                <div class="platform-info">
                  <Icon :icon="item.icon" class="platform-icon" />
                  <div class="platform-name">{{ item.platform }}</div>
                </div>
                <div class="count-bubble">
                  <span class="count-number">{{ item.count }}</span>
                  <div class="bubble-glow"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- 各平台问题占比 -->
          <div class="platform-section neon-panel">
            <div class="panel-header">
              <span class="panel-title">
                <Icon icon="mdi:chart-pie" class="panel-title-icon" />
                平台占比分析
              </span>
            </div>
            <div class="pie-chart-container">
              <div class="chart-wrapper">
                <div class="chart" ref="pieChart"></div>
                <div class="chart-center">
                  <div class="center-text">总量</div>
                  <div class="center-number">{{ totalArticles }}</div>
                </div>
              </div>
              <div class="chart-legend">
                <div v-if="platformStatsLoading" class="loading-state">
                  <div class="loading-spinner"></div>
                </div>
                <div v-else-if="platformStats.length === 0" class="empty-state">
                  <Icon icon="mdi:chart-pie" class="empty-icon" />
                  <span>暂无数据</span>
                </div>
                <div v-else class="legend-item" v-for="(item, index) in platformStats" :key="index"
                  @mouseenter="highlightPieChart(index)" @mouseleave="resetPieChart">
                  <div class="legend-marker" :style="{ backgroundColor: item.color }">
                    <div class="marker-glow"></div>
                  </div>
                  <div class="legend-content">
                    <div class="platform-name">{{ item.platform }}</div>
                    <div class="platform-stats">
                      <span class="percentage">{{ item.percentage }}%</span>
                      <span class="count">({{ item.count }})</span>
                    </div>
                  </div>
                  <div class="legend-trend" :class="item.trend">
                    <Icon :icon="getTrendIcon(item.trend)" class="trend-icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 报表明细 -->
      <div class="report-details neon-panel">
        <div class="details-header">
          <div class="header-left">
            <span class="details-title">
              <Icon icon="mdi:chart-line" class="details-title-icon" />
              实时报表明细
            </span>
            <div class="live-indicator">
              <div class="live-dot"></div>
              <span class="live-text">LIVE</span>
            </div>
          </div>
          <div class="header-right">
            <div class="update-time">最后更新: {{ currentTime }}</div>
            <button class="export-btn" @click="exportReport">
              <Icon icon="mdi:download" class="export-icon" />
              导出报表
            </button>
            <button class="refresh-btn" @click="loadAllData">
              <Icon icon="mdi:refresh" class="refresh-icon" />
              刷新
            </button>
          </div>
        </div>
        <div class="details-content">
          <div class="table-container">
            <el-table :data="reportData" v-loading="reportLoading" class="report-table" :header-cell-style="{
              background: 'transparent',
              color: '#e0e6ff',
              borderBottom: '1px solid rgba(255, 112, 72, 0.3)'
            }">
              <el-table-column prop="id" label="排名" width="80">
                <template #default="{ $index }">
                  <div class="rank-cell" :class="`rank-${$index + 1}`">
                    {{ $index + 1 }}
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="keyword" label="文章标题">
                <template #default="{ row }">
                  <div class="keyword-cell">
                    <span class="keyword-text">{{ row.keyword }}</span>
                    <div class="keyword-tags">
                      <span class="keyword-tag" v-if="row.hot">
                        <Icon icon="mdi:fire" class="tag-icon" />
                      </span>
                      <span class="keyword-tag" v-if="row.new">
                        <Icon icon="mdi:new-box" class="tag-icon" />
                      </span>
                    </div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="type" label="类型">
                <template #default="{ row }">
                  <span class="type-badge" :class="row.type">
                    <Icon :icon="row.type === 'core' ? 'mdi:diamond-stone' : 'mdi:flask-outline'" class="type-icon" />
                    {{ row.type === 'core' ? '核心' : '蒸馏' }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="platform" label="发布平台">
                <template #default="{ row }">
                  <div class="platform-cell">
                    <Icon :icon="getPlatformIcon(row.platform)" class="platform-icon" />
                    <span class="platform-name">{{ row.platform }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="count" label="数量">
                <template #default="{ row }">
                  <div class="count-cell">
                    <span class="count-number">{{ row.count }}</span>
                    <div class="count-sparkles">
                      <span class="sparkle" v-for="n in 3" :key="n">
                        <Icon icon="mdi:sparkles" class="sparkle-icon" />
                      </span>
                    </div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="trend" label="趋势">
                <template #default="{ row }">
                  <div class="trend-cell" :class="row.trend">
                    <Icon :icon="getTrendIcon(row.trend)" class="trend-icon" />
                    <span class="trend-value">{{ row.change }}%</span>
                    <div class="trend-glow"></div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="date" label="更新时间" />
            </el-table>

            <!-- 分页 -->
            <div class="pagination">
              <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                :page-sizes="[10, 20, 50, 100]" :total="pagination.total"
                layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
                @current-change="handleCurrentChange" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部装饰 -->
    <div class="footer-decoration">
      <div class="scan-line"></div>
      <div class="data-stream">
        <span v-for="n in 20" :key="n" class="data-bit">0101</span>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, computed, nextTick } from 'vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'
import { Icon } from '@iconify/vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
  name: 'AiReport',
  components: {
    Icon
  },
  setup() {
    const activeTab = ref('search')
    const pieChart = ref(null)
    let chartInstance = null

    // 用户信息
    const userInfo = computed(() => {
      return JSON.parse(Cookies.get('user_info') || '{}')
    })

    // 加载状态
    const dashboardLoading = ref(false)
    const coreRankingsLoading = ref(false)
    const platformStatsLoading = ref(false)
    const trainingStatsLoading = ref(false)
    const reportLoading = ref(false)

    // 数据状态
    const dashboardStats = ref([])
    const coreRankings = ref([])
    const platformStats = ref([])
    const trainingStats = ref([])
    const reportData = ref([])

    // 分页
    const pagination = ref({
      page: 1,
      pageSize: 10,
      total: 0
    })

    // 当前时间
    const currentTime = computed(() => {
      return new Date().toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    })

    // 总文章数
    const totalArticles = computed(() => {
      return platformStats.value.reduce((total, item) => total + item.count, 0)
    })

    // 选项卡数据
    const tabs = [
      { id: 'search', label: '搜索分析', icon: 'mdi:magnify' },
      { id: 'qa', label: '问答洞察', icon: 'mdi:comment-text-outline' },
      { id: 'brand', label: '品牌报表', icon: 'mdi:tag-outline' }
    ]

    // 获取平台图标
    const getPlatformIcon = (platform) => {
      const icons = {
        '自媒体平台': 'mdi:account-circle',
        '第三方媒体': 'mdi:share-variant',
        'B2B平台': 'mdi:office-building',
        '智能体官网': 'mdi:robot',
        '其他': 'mdi:dots-horizontal'
      }
      return icons[platform] || 'mdi:cellphone'
    }

    // 获取趋势图标
    const getTrendIcon = (trend) => {
      const icons = {
        'up': 'mdi:trending-up',
        'down': 'mdi:trending-down',
        'stable': 'mdi:trending-neutral'
      }
      return icons[trend] || 'mdi:chart-bar'
    }

    // 加载仪表板数据
    const loadDashboardStats = async () => {
      dashboardLoading.value = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/site/api/report_api.php`,
          {
            action: 'get_dashboard_stats',
            store_id: userInfo.value.id
          }
        )

        if (response.data.success) {
          const data = response.data.data

          dashboardStats.value = [
            {
              label: '总任务数',
              value: data.total_tasks.toLocaleString(),
              change: data.trends.tasks,
              trend: data.trends.tasks >= 0 ? 'up' : 'down',
              icon: 'mdi:clipboard-list'
            },
            {
              label: '总文章数',
              value: data.total_articles.toLocaleString(),
              change: data.trends.articles,
              trend: data.trends.articles >= 0 ? 'up' : 'down',
              icon: 'mdi:file-document'
            },
            {
              label: '总发布数',
              value: data.total_published.toLocaleString(),
              change: data.trends.published,
              trend: data.trends.published >= 0 ? 'up' : 'down',
              icon: 'mdi:send'
            }
          ]
        } else {
          ElMessage.error(response.data.error || '加载仪表板数据失败')
        }
      } catch (error) {
        console.error('加载仪表板数据失败:', error)
        ElMessage.error('加载仪表板数据失败')
      } finally {
        dashboardLoading.value = false
      }
    }

    // 加载核心词榜单
    const loadCoreRankings = async () => {
      coreRankingsLoading.value = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/site/api/report_api.php`,
          {
            action: 'get_core_rankings',
            store_id: userInfo.value.id,
            limit: 3
          }
        )

        if (response.data.success) {
          coreRankings.value = response.data.data
        } else {
          ElMessage.error(response.data.error || '加载核心词榜单失败')
        }
      } catch (error) {
        console.error('加载核心词榜单失败:', error)
        ElMessage.error('加载核心词榜单失败')
      } finally {
        coreRankingsLoading.value = false
      }
    }

    // 加载平台统计数据
    const loadPlatformStats = async () => {
      platformStatsLoading.value = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/site/api/report_api.php`,
          {
            action: 'get_platform_stats',
            store_id: userInfo.value.id
          }
        )

        if (response.data.success) {
          platformStats.value = response.data.data
          initPieChart()
        } else {
          ElMessage.error(response.data.error || '加载平台统计数据失败')
        }
      } catch (error) {
        console.error('加载平台统计数据失败:', error)
        ElMessage.error('加载平台统计数据失败')
      } finally {
        platformStatsLoading.value = false
      }
    }

    // 加载训练统计
    const loadTrainingStats = async () => {
      trainingStatsLoading.value = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/site/api/report_api.php`,
          {
            action: 'get_training_stats',
            store_id: userInfo.value.id
          }
        )

        if (response.data.success) {
          trainingStats.value = response.data.data
        } else {
          ElMessage.error(response.data.error || '加载训练统计失败')
        }
      } catch (error) {
        console.error('加载训练统计失败:', error)
        ElMessage.error('加载训练统计失败')
      } finally {
        trainingStatsLoading.value = false
      }
    }

    // 加载报表明细
    const loadReportDetails = async () => {
      reportLoading.value = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/site/api/report_api.php`,
          {
            action: 'get_report_details',
            store_id: userInfo.value.id,
            page: pagination.value.page,
            page_size: pagination.value.pageSize
          }
        )

        if (response.data.success) {
          reportData.value = response.data.data
          pagination.value.total = response.data.pagination.total
        } else {
          ElMessage.error(response.data.error || '加载报表明细失败')
        }
      } catch (error) {
        console.error('加载报表明细失败:', error)
        ElMessage.error('加载报表明细失败')
      } finally {
        reportLoading.value = false
      }
    }

    // 初始化饼图
    const initPieChart = () => {
      if (!pieChart.value || platformStats.value.length === 0) return

      nextTick(() => {
        chartInstance = echarts.init(pieChart.value)
        const option = {
          tooltip: {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c}%',
            backgroundColor: 'rgba(13, 5, 26, 0.9)',
            borderColor: '#6E21FE',
            textStyle: {
              color: '#E0E6FF'
            }
          },
          series: [
            {
              name: '平台占比',
              type: 'pie',
              radius: ['60%', '80%'],
              avoidLabelOverlap: false,
              itemStyle: {
                borderColor: '#0D051A',
                borderWidth: 3,
                borderRadius: 8,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
                shadowBlur: 10
              },
              label: {
                show: false
              },
              emphasis: {
                scale: true,
                scaleSize: 10,
                itemStyle: {
                  shadowBlur: 20,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              },
              data: platformStats.value.map(item => ({
                value: item.percentage,
                name: item.platform,
                itemStyle: {
                  color: {
                    type: 'linear',
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops: [
                      { offset: 0, color: item.color },
                      { offset: 1, color: item.color + '80' }
                    ]
                  }
                }
              }))
            }
          ]
        }

        chartInstance.setOption(option)

        // 响应式调整
        const resizeChart = () => chartInstance.resize()
        window.addEventListener('resize', resizeChart)

        return () => window.removeEventListener('resize', resizeChart)
      })
    }

    // 高亮饼图
    const highlightPieChart = (index) => {
      if (chartInstance) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: index
        })
      }
    }

    // 重置饼图
    const resetPieChart = () => {
      if (chartInstance) {
        chartInstance.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: 0
        })
      }
    }

    // 选项卡切换
    const handleTabChange = (tabId) => {
      activeTab.value = tabId
      // 这里可以根据不同的选项卡加载不同的数据
      loadAllData()
    }

    // 分页大小变化
    const handleSizeChange = (size) => {
      pagination.value.pageSize = size
      pagination.value.page = 1
      loadReportDetails()
    }

    // 当前页变化
    const handleCurrentChange = (page) => {
      pagination.value.page = page
      loadReportDetails()
    }

    // 加载所有数据
    const loadAllData = () => {
      loadDashboardStats()
      loadCoreRankings()
      loadPlatformStats()
      loadTrainingStats()
      loadReportDetails()
    }

    // 导出报表
    const exportReport = () => {
      ElMessage.success('报表导出功能开发中...')
    }

    onMounted(() => {
      loadAllData()
    })

    return {
      activeTab,
      tabs,
      dashboardStats,
      coreRankings,
      platformStats,
      trainingStats,
      reportData,
      pieChart,
      currentTime,
      pagination,
      totalArticles,
      coreRankingsLoading,
      platformStatsLoading,
      trainingStatsLoading,
      reportLoading,
      getPlatformIcon,
      getTrendIcon,
      highlightPieChart,
      resetPieChart,
      loadCoreRankings,
      handleTabChange,
      handleSizeChange,
      handleCurrentChange,
      loadAllData,
      exportReport
    }
  }
}
</script>


<style scoped lang="scss">
.ai-report-container {
  padding: 24px;
  min-height: calc(100vh - 70px);
  background: linear-gradient(135deg, #0D051A 0%, #1A0B2E 50%, #2D1B47 100%);
  position: relative;
  overflow: hidden;

  // 动态星空背景
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(2px 2px at 20px 30px, #6E21FE, transparent),
      radial-gradient(2px 2px at 40px 70px, #FF7048, transparent),
      radial-gradient(1px 1px at 90px 40px, #00F2FE, transparent),
      radial-gradient(1px 1px at 130px 80px, #6E21FE, transparent),
      radial-gradient(2px 2px at 160px 30px, #FF7048, transparent);
    background-repeat: repeat;
    animation: twinkle 4s ease-in-out infinite alternate;
    pointer-events: none;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #A0A8B8;

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(110, 33, 254, 0.3);
    border-top: 3px solid #6E21FE;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #A0A8B8;

  .empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px 0;
}

.refresh-btn {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #E0E6FF;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #6E21FE;
    transform: translateY(-1px);

    .refresh-icon {
      transform: rotate(180deg);
    }
  }

  .refresh-icon {
    font-size: 16px;
    transition: transform 0.3s ease;
  }
}

@keyframes twinkle {
  0% {
    opacity: 0.3;
    transform: translateY(0px);
  }

  100% {
    opacity: 0.8;
    transform: translateY(-10px);
  }
}

// 动态背景效果
.bg-effects {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  animation: float 6s ease-in-out infinite;

  &.orb-1 {
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, #6E21FE 0%, transparent 70%);
    top: 10%;
    left: 10%;
    animation-delay: 0s;
  }

  &.orb-2 {
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, #FF7048 0%, transparent 70%);
    top: 60%;
    right: 10%;
    animation-delay: 2s;
  }

  &.orb-3 {
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, #00F2FE 0%, transparent 70%);
    bottom: 20%;
    left: 20%;
    animation-delay: 4s;
  }
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 500px;
  height: 500px;
  border: 2px solid rgba(110, 33, 254, 0.3);
  border-radius: 50%;
  animation: pulse 3s ease-out infinite;
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0px) scale(1);
  }

  50% {
    transform: translateY(-20px) scale(1.1);
  }
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 1;
  }

  100% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0;
  }
}

.main-content {
  position: relative;
  z-index: 1;
  background: rgba(13, 5, 26, 0.7);
  border-radius: 24px;
  padding: 32px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(110, 33, 254, 0.3);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

// 霓虹面板样式
.neon-panel {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
        rgba(110, 33, 254, 0.1) 0%,
        rgba(255, 112, 72, 0.05) 50%,
        rgba(0, 242, 254, 0.1) 100%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg,
        #6E21FE, #FF7048, #00F2FE, #6E21FE);
    border-radius: 18px;
    z-index: -1;
    opacity: 0.3;
    filter: blur(10px);
  }
}

// 图标基础样式
.tab-icon,
.total-icon,
.panel-title-icon,
.action-icon,
.platform-icon,
.details-title-icon,
.export-icon,
.trend-icon,
.type-icon,
.tag-icon,
.sparkle-icon,
.crown-icon {
  transition: all 0.3s ease;
}

.tab-group {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
  justify-content: center;
}

.tab-button {
  position: relative;
  padding: 12px 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  color: #A0A8B8;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;

  &:hover {
    border-color: #6E21FE;
    color: #E0E6FF;
    transform: translateY(-2px);

    .tab-icon {
      transform: scale(1.1);
    }
  }

  &.active {
    background: linear-gradient(135deg, #6E21FE, #FF7048);
    border-color: transparent;
    color: #FFFFFF;
    box-shadow: 0 8px 25px rgba(110, 33, 254, 0.4);

    .tab-glow {
      opacity: 1;
    }

    .tab-icon {
      color: white;
    }
  }
}

.tab-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.4) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.overview-section {
  margin-bottom: 32px;
}

.data-total {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;

  .total-item {
    position: relative;
    padding: 24px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    gap: 16px;
    animation: slideInUp 0.6s ease-out var(--delay) both;

    .total-icon {
      font-size: 32px;
      color: #6E21FE;
      filter: drop-shadow(0 0 10px currentColor);
    }

    .total-content {
      flex: 1;

      .total-label {
        font-size: 14px;
        color: #A0A8B8;
        margin-bottom: 4px;
      }

      .total-value {
        font-size: 32px;
        font-weight: 800;
        background: linear-gradient(135deg, #00F2FE 0%, #6E21FE 50%, #FF7048 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        margin-bottom: 4px;
      }

      .total-trend {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;

        &.up {
          color: #00F2FE;
        }

        &.down {
          color: #FF7048;
        }

        .trend-icon {
          font-size: 14px;
        }
      }
    }
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.content-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

.panel-header {
  position: relative;
  padding: 20px 24px;
  background: rgba(110, 33, 254, 0.1);
  border-bottom: 1px solid rgba(110, 33, 254, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;

  .panel-title {
    color: #E0E6FF;
    font-size: 16px;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(110, 33, 254, 0.5);
    display: flex;
    align-items: center;
    gap: 8px;

    .panel-title-icon {
      font-size: 18px;
    }
  }

  .action-btn {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 6px;
    color: #A0A8B8;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: #6E21FE;
      color: #E0E6FF;
      transform: rotate(90deg);
    }

    .action-icon {
      font-size: 16px;
    }
  }
}

// 排名列表样式
.ranking-list {
  padding: 16px;

  .ranking-item {
    position: relative;
    display: flex;
    align-items: center;
    padding: 16px;
    margin-bottom: 12px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;
    animation: slideInLeft 0.6s ease-out calc(var(--rank) * 0.1s) both;

    &:hover {
      border-color: rgba(110, 33, 254, 0.3);
      transform: translateX(8px);

      .rank-sparkle {
        opacity: 1;
      }
    }

    .rank-badge {
      position: relative;
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, #6E21FE, #FF7048);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 16px;
      font-weight: 700;
      color: white;

      &.rank-1 {
        background: linear-gradient(135deg, #FFD700, #FF6B00);
      }

      &.rank-2 {
        background: linear-gradient(135deg, #C0C0C0, #808080);
      }

      &.rank-3 {
        background: linear-gradient(135deg, #CD7F32, #8B4513);
      }

      .rank-crown {
        position: absolute;
        top: -8px;
        right: -8px;

        .crown-icon {
          font-size: 12px;
          color: #FFD700;
        }
      }
    }

    .rank-content {
      flex: 1;

      .name {
        color: #E0E6FF;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
      }

      .progress-bar {
        width: 100%;
        height: 4px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 2px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #00F2FE, #6E21FE);
          border-radius: 2px;
          transition: width 1s ease-out;
        }
      }
    }

    .count {
      color: #FF7048;
      font-weight: 700;
      font-size: 16px;
    }
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

// 蒸馏词样式
.distilled-content {
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  .distilled-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    animation: bounceIn 0.6s ease-out calc(var(--item-index) * 0.1s) both;

    .platform-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .platform-icon {
        font-size: 20px;
        color: #6E21FE;
      }

      .platform-name {
        color: #E0E6FF;
        font-weight: 600;
      }
    }

    .count-bubble {
      position: relative;
      padding: 8px 16px;
      background: linear-gradient(135deg, #6E21FE, #FF7048);
      border-radius: 20px;
      color: white;
      font-weight: 700;

      .bubble-glow {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at center, rgba(255, 255, 255, 0.4) 0%, transparent 70%);
        border-radius: 20px;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      &:hover .bubble-glow {
        opacity: 1;
      }
    }
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }

  50% {
    opacity: 1;
    transform: scale(1.05);
  }

  70% {
    transform: scale(0.9);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

// 饼图容器
.pie-chart-container {
  padding: 20px;
  display: flex;
  gap: 24px;
  align-items: center;
}

.chart-wrapper {
  position: relative;
  width: 160px;
  height: 160px;

  .chart {
    width: 100%;
    height: 100%;
  }

  .chart-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;

    .center-text {
      color: #A0A8B8;
      font-size: 12px;
      margin-bottom: 2px;
    }

    .center-number {
      color: #E0E6FF;
      font-weight: 700;
      font-size: 16px;
    }
  }
}

.chart-legend {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      border-color: rgba(110, 33, 254, 0.3);
      transform: translateX(8px);
    }

    .legend-marker {
      position: relative;
      width: 16px;
      height: 16px;
      border-radius: 4px;

      .marker-glow {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at center, rgba(255, 255, 255, 0.6) 0%, transparent 70%);
        border-radius: 4px;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      &:hover .marker-glow {
        opacity: 1;
      }
    }

    .legend-content {
      flex: 1;

      .platform-name {
        color: #E0E6FF;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 2px;
      }

      .platform-stats {
        display: flex;
        gap: 8px;

        .percentage {
          color: #FF7048;
          font-weight: 700;
          font-size: 12px;
        }

        .count {
          color: #A0A8B8;
          font-size: 12px;
        }
      }
    }

    .legend-trend {
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 700;

      &.up {
        background: rgba(0, 242, 254, 0.2);
        color: #00F2FE;
      }

      &.down {
        background: rgba(255, 112, 72, 0.2);
        color: #FF7048;
      }

      &.stable {
        background: rgba(160, 168, 184, 0.2);
        color: #A0A8B8;
      }

      .trend-icon {
        font-size: 14px;
      }
    }
  }
}

// 报表明细样式
.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: rgba(110, 33, 254, 0.1);
  border-bottom: 1px solid rgba(110, 33, 254, 0.2);

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;

    .details-title {
      color: #E0E6FF;
      font-size: 18px;
      font-weight: 700;
      text-shadow: 0 0 10px rgba(110, 33, 254, 0.5);
      display: flex;
      align-items: center;
      gap: 8px;

      .details-title-icon {
        font-size: 20px;
      }
    }

    .live-indicator {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      background: rgba(255, 112, 72, 0.2);
      border-radius: 12px;
      border: 1px solid rgba(255, 112, 72, 0.3);

      .live-dot {
        width: 6px;
        height: 6px;
        background: #FF7048;
        border-radius: 50%;
        animation: pulse 2s infinite;
      }

      .live-text {
        color: #FF7048;
        font-size: 12px;
        font-weight: 700;
      }
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;

    .update-time {
      color: #A0A8B8;
      font-size: 13px;
    }

    .export-btn {
      padding: 8px 16px;
      background: linear-gradient(135deg, #6E21FE, #FF7048);
      border: none;
      border-radius: 8px;
      color: white;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(110, 33, 254, 0.4);

        .export-icon {
          transform: translateY(-1px);
        }
      }

      .export-icon {
        font-size: 16px;
      }
    }
  }
}

.details-content {
  padding: 20px;
}

// 表格单元格样式
.rank-cell {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #6E21FE, #FF7048);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 14px;

  &.rank-1 {
    background: linear-gradient(135deg, #FFD700, #FF6B00);
  }

  &.rank-2 {
    background: linear-gradient(135deg, #C0C0C0, #808080);
  }

  &.rank-3 {
    background: linear-gradient(135deg, #CD7F32, #8B4513);
  }
}

.keyword-cell {
  display: flex;
  align-items: center;
  gap: 8px;

  .keyword-text {
    color: #E0E6FF;
    font-weight: 600;
  }

  .keyword-tags {
    display: flex;
    gap: 4px;

    .keyword-tag {
      .tag-icon {
        font-size: 14px;

        &:first-child {
          color: #FF7048;
        }

        &:last-child {
          color: #00F2FE;
        }
      }
    }
  }
}

.type-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;

  &.core {
    background: linear-gradient(135deg, #6E21FE, #FF7048);
    color: white;
  }

  &.distilled {
    background: linear-gradient(135deg, #00F2FE, #4ECDC4);
    color: white;
  }

  .type-icon {
    font-size: 14px;
  }
}

.platform-cell {
  display: flex;
  align-items: center;
  gap: 8px;

  .platform-icon {
    font-size: 16px;
    color: #6E21FE;
  }

  .platform-name {
    color: #E0E6FF;
  }
}

.count-cell {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;

  .count-number {
    color: #FF7048;
    font-weight: 700;
  }

  .count-sparkles {
    display: flex;

    .sparkle {
      opacity: 0;
      animation: sparkle 2s infinite;

      &:nth-child(1) {
        animation-delay: 0s;
      }

      &:nth-child(2) {
        animation-delay: 0.5s;
      }

      &:nth-child(3) {
        animation-delay: 1s;
      }

      .sparkle-icon {
        font-size: 10px;
        color: #00F2FE;
      }
    }
  }
}

@keyframes sparkle {

  0%,
  100% {
    opacity: 0;
    transform: scale(0);
  }

  50% {
    opacity: 1;
    transform: scale(1);
  }
}

.trend-cell {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;

  &.up {
    background: rgba(0, 242, 254, 0.2);
    color: #00F2FE;
  }

  &.down {
    background: rgba(255, 112, 72, 0.2);
    color: #FF7048;
  }

  &.stable {
    background: rgba(160, 168, 184, 0.2);
    color: #A0A8B8;
  }

  .trend-icon {
    font-size: 14px;
  }
}

// 底部装饰
.footer-decoration {
  position: relative;
  margin-top: 32px;

  .scan-line {
    height: 2px;
    background: linear-gradient(90deg, transparent, #6E21FE, #FF7048, #00F2FE, transparent);
    animation: scan 3s linear infinite;
  }

  .data-stream {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;

    .data-bit {
      color: rgba(160, 168, 184, 0.3);
      font-size: 10px;
      font-family: monospace;
      animation: dataFlow 2s linear infinite;

      &:nth-child(odd) {
        animation-delay: 1s;
      }
    }
  }
}

@keyframes scan {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

@keyframes dataFlow {
  0% {
    opacity: 0.3;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.3;
  }
}

// 响应式设计
@media (max-width: 1024px) {
  .content-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .ai-report-container {
    padding: 16px;
  }

  .main-content {
    padding: 20px;
  }

  .data-total {
    grid-template-columns: 1fr;
  }

  .pie-chart-container {
    flex-direction: column;
  }

  .distilled-content {
    grid-template-columns: 1fr;
  }

  .details-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .tab-group {
    flex-wrap: wrap;
  }

  .tab-button {
    flex: 1;
    min-width: 120px;
    justify-content: center;
  }
}
</style>

<style>
/* 全局表格样式覆盖 */
.ai-report-container .el-table {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(255, 255, 255, 0.05) !important;
  --el-table-current-row-bg-color: rgba(255, 112, 72, 0.1) !important;
  --el-table-header-text-color: #e0e6ff !important;
  --el-table-text-color: #e0e6ff !important;
  --el-table-border-color: rgba(255, 112, 72, 0.3) !important;
}

.ai-report-container .el-table th,
.ai-report-container .el-table tr,
.ai-report-container .el-table td {
  background: transparent !important;
  color: #e0e6ff !important;
  border-bottom: 1px solid rgba(255, 112, 72, 0.1) !important;
}

.ai-report-container .el-table th {
  border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.ai-report-container .el-table .el-table__inner-wrapper::before {
  background-color: rgba(255, 112, 72, 0.3) !important;
}
</style>